import React, {useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import Copy from '../../components/cms/copy'
import Heading from '../../components/cms/heading'
import ModuleContainer from '../../components/cms/module-container'
import ProductAnimated from './product-animated'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import productAnimatedModuleRecipe from './index.recipe'
import {useScroll} from 'framer-motion'
import useResizeObserver from '../../../hooks/use-resize-observer'

const ProductAnimatedModule = ({
    moduleId,
    moduleType,
    pageId,
    anchor,
    heading,
    headingLevel,
    copy,
    animatedProduct,
    containerHeight,
    background,
    desktopDisplay = 'product-below-copy'
}) => {
    const layout = desktopDisplay
    const {subline, headline1, headline2, variant} = heading
    const ariaLabel = `${subline} - ${headline1} ${headline2}`

    const recipe = useSlotRecipe({recipe: productAnimatedModuleRecipe})
    const styles = useMemo(() => {
        return recipe({layout})
    }, [layout, recipe])

    const [contentEl, setContentEl] = useState(null)
    const [contentHeight, setContentHeight] = useState(0)
    const ref = useRef(null)
    const {scrollYProgress} = useScroll({
        target: ref,
        offset: ['start start', 'end end']
    })

    useResizeObserver(contentEl, () => setContentHeight(contentEl.clientHeight))

    return (
        <ModuleContainer
            moduleId={moduleId}
            moduleType={moduleType}
            pageId={pageId}
            anchor={anchor}
            background={background}
            css={styles.container}
            aria-label={variant !== 'hidden' ? undefined : ariaLabel}
            spacing="none"
            {...{
                '--content-height': `${contentHeight}px`
            }}
        >
            <Box
                width="100%"
                height="300vh"
                ref={ref}
                // compute extra space so the module can fill the screen in case it is smaller
                // use margin so the animation finishes before this spacing start to move away
                marginBottom="calc(max(0px, 100vh - var(--content-height, 0px) - var(--header-height, 0px) - var(--padding-bottom)))"
            >
                <Box
                    ref={setContentEl}
                    css={styles.stickyContainer}
                    position="sticky"
                    // need to compute where to stick so the whole image is visible in case the screen is small
                    // otherwise just stick below the header
                    top="calc(min(var(--header-height, 0px), 100vh - var(--content-height, 0px)))"
                >
                    <Box css={styles.textBox}>
                        <Box css={styles.textContent}>
                            <Heading headingLevel={headingLevel} {...heading} alignment="inherit" />
                            {copy?.text && <Copy {...copy} alignment="inherit"></Copy>}
                        </Box>
                    </Box>
                    <Box css={styles.productBox}>
                        <ProductAnimated
                            {...animatedProduct}
                            scrollYProgress={scrollYProgress}
                            containerHeight={containerHeight}
                        />
                    </Box>
                </Box>
            </Box>
        </ModuleContainer>
    )
}

ProductAnimatedModule.propTypes = {
    anchor: PropTypes.string,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    headingLevel: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
    heading: PropTypes.shape({
        headline1: PropTypes.string,
        headline2: PropTypes.string,
        subline: PropTypes.string,
        variant: PropTypes.string,
        alignment: PropTypes.oneOf(['left', 'center'])
    }),
    desktopDisplay: PropTypes.string,
    copy: PropTypes.shape({
        text: PropTypes.string,
        alignment: PropTypes.oneOf(['left', 'center'])
    }),
    animatedProduct: PropTypes.shape({
        imagePrefix: PropTypes.string,
        imageExtension: PropTypes.string,
        imageCount: PropTypes.number,
        imageWidth: PropTypes.number,
        imageHeight: PropTypes.number
    }),
    containerHeight: PropTypes.number,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string
}

export default ProductAnimatedModule
